import {
  poseidonAsync,
  proveFlatJoinSplitV2,
  type FlatJoinSplitV2ProveRequest,
} from "./sdk";
import { withFlatJoinSplitV2Prover } from "./prover";
import { preparePrivateFeeProver } from "./privateFeeProver";

/** The accumulator is fixed by the ordered output witnesses, not by proving.
 * Use the same Poseidon recurrence that the SDK checks on the completed proof. */
export async function plannedOutputAccumulator(
  outputs: FlatJoinSplitV2ProveRequest["outputs"],
): Promise<string> {
  let accumulator = 0n;
  for (const output of outputs) {
    const commitment = await poseidonAsync([
      BigInt(output.npk),
      BigInt(output.token_hash),
      BigInt(output.value),
    ]);
    accumulator = await poseidonAsync([accumulator, commitment]);
  }
  return accumulator.toString();
}

/** Both witnesses remain in local, disposable workers. A failure or cancellation
 * terminates both jobs; no partial pair may reach authorization or submission. */
export async function provePrivateFeePair(
  request: FlatJoinSplitV2ProveRequest,
  feeWitness: (accumulator: string) => unknown,
  options: Parameters<typeof withFlatJoinSplitV2Prover>[1] = {},
) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (options.signal?.aborted)
    throw new DOMException("Cancelled", "AbortError");
  options.signal?.addEventListener("abort", abort, { once: true });
  let feeProver: ReturnType<typeof preparePrivateFeeProver> | undefined;
  let jobs: Promise<unknown>[] = [];
  try {
    // Begin checked public-key loading while hashing the planned statement.
    feeProver = preparePrivateFeeProver(controller.signal);
    const accumulator = await plannedOutputAccumulator(request.outputs);
    if (controller.signal.aborted)
      throw new DOMException("Cancelled", "AbortError");
    const witness = feeWitness(accumulator);
    const main = withFlatJoinSplitV2Prover(
      (prover) =>
        proveFlatJoinSplitV2(prover as any, request, controller.signal),
      { ...options, signal: controller.signal },
    );
    jobs = [main];
    const fee = feeProver.prove(witness);
    jobs.push(fee);
    const [bundle, feeProof] = await Promise.all([main, fee]);
    // The SDK also checks every commitment against the main request. Bind the
    // independent fee proof to that exact completed statement before returning.
    if (BigInt(bundle.proof.commitment_accumulator) !== BigInt(accumulator)) {
      throw new Error("Transaction proof outputs do not match.");
    }
    if (controller.signal.aborted)
      throw new DOMException("Cancelled", "AbortError");
    return { bundle, feeProof };
  } finally {
    controller.abort();
    feeProver?.dispose();
    await Promise.allSettled(jobs);
    options.signal?.removeEventListener("abort", abort);
  }
}
